import {
  clearAuthTokens,
  getRefreshToken,
  storeTokens,
  storeTokenExpiresAt,
} from './tokenStorage';
import { postOidcTokenRequest } from './oidcTokenExchange';

const OIDC_CONFIG_CACHE_KEY = 'oidc_config_cache';

interface OidcConfig {
  clientId: string;
  authority: string;
  scope?: string;
  tokenEndpoint?: string;
}

interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  scope?: string;
  id_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

let refreshInFlight: Promise<boolean> | null = null;
let sessionExpiredHandled = false;
let loginListenerInstalled = false;

export function resetAuthSessionExpiredFlag(): void {
  sessionExpiredHandled = false;
}

function ensureLoginListener(): void {
  if (loginListenerInstalled || typeof window === 'undefined') {
    return;
  }
  loginListenerInstalled = true;
  window.addEventListener('oidc-login-success', () => {
    resetAuthSessionExpiredFlag();
  });
}

function getCachedOidcConfig(): OidcConfig | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(OIDC_CONFIG_CACHE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as OidcConfig;
  } catch {
    localStorage.removeItem(OIDC_CONFIG_CACHE_KEY);
    return null;
  }
}

async function exchangeRefreshToken(
  refreshToken: string,
  config: OidcConfig
): Promise<TokenResponse> {
  const tokenEndpoint =
    config.tokenEndpoint || `${config.authority}/oauth2/v2.0/token`;

  const params = new URLSearchParams({
    client_id: config.clientId,
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  if (config.scope) {
    params.set('scope', config.scope);
  }

  const response = await postOidcTokenRequest(tokenEndpoint, params);

  const data = (await response.json().catch(() => ({}))) as TokenResponse;

  if (!response.ok) {
    const message =
      data.error_description || data.error || `HTTP ${response.status}`;
    throw new Error(`Token refresh failed: ${message}`);
  }

  if (!data.access_token) {
    throw new Error('Token refresh failed: missing access_token');
  }

  return data;
}

async function performTokenRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn('No refresh token available for silent re-auth');
    return false;
  }

  const config = getCachedOidcConfig();
  if (!config?.clientId || !config.authority) {
    console.warn('OIDC configuration unavailable for token refresh');
    return false;
  }

  try {
    const tokenData = await exchangeRefreshToken(refreshToken, config);

    storeTokens({
      accessToken: tokenData.access_token,
      idToken: tokenData.id_token,
      refreshToken: tokenData.refresh_token,
    });

    if (tokenData.expires_in) {
      storeTokenExpiresAt(tokenData.expires_in);
    }

    console.log('Access token refreshed successfully');
    return true;
  } catch (error) {
    console.error(
      'Silent token refresh failed:',
      error instanceof Error ? error.message : error
    );
    return false;
  }
}

/**
 * Refresh the OIDC access token using the stored refresh token.
 * Concurrent callers share a single in-flight refresh request.
 */
export async function refreshAccessToken(): Promise<boolean> {
  ensureLoginListener();

  if (refreshInFlight) {
    return refreshInFlight;
  }

  const promise = performTokenRefresh().finally(() => {
    if (refreshInFlight === promise) {
      refreshInFlight = null;
    }
  });

  refreshInFlight = promise;
  return promise;
}

/**
 * Clear session and redirect to login when refresh is not possible.
 */
export function handleAuthSessionExpired(): void {
  if (sessionExpiredHandled) {
    return;
  }
  sessionExpiredHandled = true;

  clearAuthTokens();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('oidc-auth-expired'));

    const path = window.location.pathname;
    if (path !== '/login' && path !== '/auth/callback') {
      window.location.href = '/login';
    }
  }
}
